import { createMutation } from 'react-query-kit';
import { type AxiosRequestConfig } from 'axios';

import { type ApiQuizDifficulty } from '@/shared/features/ai-quiz-generation/constants';
import { type QuizType } from '@/types';

import { AI_HTTP_CLIENT } from './client';
import { normalizeAnswers } from './normalizeAnswers';
import {
  type QuizGenerateItem,
  type UseGenerateQuizRequest,
  type UseGenerateQuizResponse,
} from './type';

export type QuizTypeOption = QuizType | 'mixed';

export const createQuizAiBuildMutation = <T extends UseGenerateQuizRequest>(
  endPoint: string,
  transformRequest?: (variables: T) => any,
  customHeaders?: Record<string, string>,
) =>
  createMutation<UseGenerateQuizResponse, T>({
    mutationFn: _variables => {
      const parsedQuizOption = (
        _questions?: (QuizGenerateItem & { answers?: string[] })[],
      ): QuizGenerateItem[] =>
        (_questions ?? []).map(d => {
          const answers = normalizeAnswers(d);
          if (d.type === 'ox') {
            return { ...d, answer: answers.map(a => a.toLowerCase()) };
          }
          return { ...d, answer: answers };
        });

      // generationMode 포함 모든 camelCase 키는 client.ts request interceptor 의
      // objectToSnake 에 의해 wire 직전에 snake_case 로 변환된다.
      const transformedVariables = transformRequest
        ? transformRequest(_variables)
        : _variables;

      return AI_HTTP_CLIENT.post<{
        questions: (QuizGenerateItem & { answers?: string[] })[];
        source_length: number;
        used_source_length: number;
        generationId: string;
      }>(endPoint, transformedVariables, {
        headers: {
          ...AI_HTTP_CLIENT.defaults.headers,
          ...customHeaders,
        } as AxiosRequestConfig['headers'],
      }).then(res => ({
        questions: parsedQuizOption(res.data.questions || []),
        sourceLength: res.data.source_length || 0,
        usedSourceLength: res.data.used_source_length || 0,
        generationId: res.data.generationId,
      }));
    },
  });

export type UseBuildAiQuizByQueryRequest = UseGenerateQuizRequest & {
  userQuery: string;
  topicId?: string | null;
  type: QuizTypeOption;
  difficulty: ApiQuizDifficulty;
  previousOutput?: string;
  quizCount: number;
  tryCount: number;
  selectedStandardCodes?: string[];
  generationId?: string;
};

export const useBuildAiQuizByQuery =
  createQuizAiBuildMutation<UseBuildAiQuizByQueryRequest>(
    '/quiz-builder/generate/query',
    undefined,
    {
      'Content-Type': 'application/json',
    },
  );

export type UseBuildAiQuizByFileRequest = UseGenerateQuizRequest & {
  file: File;
  type: QuizTypeOption;
  difficulty: ApiQuizDifficulty;
  previousOutput?: string;
  quizCount: number;
  tryCount: number;
  userPrompt?: string;
  selectedStandardCodes?: string[];
  generationId?: string;
};

export type UseBuildAiQuizRequest =
  | UseBuildAiQuizByQueryRequest
  | UseBuildAiQuizByFileRequest;

export const useBuildAiQuizByFile =
  createQuizAiBuildMutation<UseBuildAiQuizByFileRequest>(
    '/quiz-builder/generate/file',
    undefined,
    {
      'Content-Type': 'multipart/form-data',
    },
  );

export type UseBuildAiQuizByYoutubeRequest = UseGenerateQuizRequest & {
  youtubeVideoId: string;
  type: QuizTypeOption;
  difficulty: ApiQuizDifficulty;
  previousOutput?: string;
  quizCount: number;
  tryCount: number;
  userPrompt?: string;
  selectedStandardCodes?: string[];
  generationId?: string;
};

export const useBuildAiQuizByYoutube =
  createQuizAiBuildMutation<UseBuildAiQuizByYoutubeRequest>(
    '/quiz-builder/generate/youtube',
    undefined,
    {
      'Content-Type': 'application/json',
    },
  );

export type UseBuildAiQuizByWebsiteRequest = UseGenerateQuizRequest & {
  websiteUrl: string;
  type: QuizTypeOption;
  difficulty: ApiQuizDifficulty;
  previousOutput?: string;
  quizCount: number;
  tryCount: number;
  userPrompt?: string;
  selectedStandardCodes?: string[];
  generationId?: string;
  /**
   * Preview 응답으로 받은 scrape_id. 백엔드는 이 값으로 S3 캐시에서 본문을
   * 로드해 재 scrape를 건너뛴다. 미제공 시 백엔드가 다시 fetch한다.
   */
  scrapeId?: string;
};

export const useBuildAiQuizByWebsite =
  createQuizAiBuildMutation<UseBuildAiQuizByWebsiteRequest>(
    '/quiz-builder/generate/website',
    undefined,
    {
      'Content-Type': 'application/json',
    },
  );
